import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { useModal } from 'hooks/useModal';
import makeStyles from '@mui/styles/makeStyles';
import { isDesktop, isMobile } from 'utils/contentViewType';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ContentViewType, ModalsKeys, Nullable } from 'global';
import { Invoice, InvoiceDetails, useInvoice } from 'modules/invoice';
import { UploadedFileView } from 'components/shared/UploadedFileView/UploadedFileView';

interface Props {
  voId: string;
  dynamicsVOId: string;
  invoiceDoc: any;
  refetchDocs: any;
  handleChangeDoc: any;
  isLoading?: boolean;
  invoice: Nullable<Invoice>;
  handleInvoiceDownload: any;
  contentViewType: ContentViewType;
  handleOpenInvoiceDetailModal: any;
  handleCloseUploadInvoiceModal: any;
  equipmentDetails?: Nullable<ReactNode>;
  isUserHaveAccessToInvoiceAndEquipmentV2: boolean;
}

export const InvoiceTab = ({
  voId,
  invoice,
  dynamicsVOId,
  invoiceDoc,
  contentViewType,
  equipmentDetails,
  isLoading = false,
  handleInvoiceDownload,
  handleOpenInvoiceDetailModal,
  isUserHaveAccessToInvoiceAndEquipmentV2,
}: Props) => {
  const { isDeleting, handleDeleteInvoiceDocument } = useInvoice();

  const { handleOpen: handleOpenCreateAndSendQuoteModal } = useModal(ModalsKeys.CreateAndSendQuote);
  const { handleOpen: handleOpenUploadInvoiceDialog } = useModal(ModalsKeys.UploadInvoiceDialog);

  const classes = useStyles();

  return (
    <>
      <Box className={classes.container}>
        <Box
          className={clsx(classes.panel, {
            [classes.panelDesktop]: isDesktop(contentViewType),
            [classes.panelMobile]: isMobile(contentViewType),
          })}
        >
          <Typography component="span" variant="subtitle2">
            Details
          </Typography>

          <Stack direction="row" gap="0.5rem">
            {isUserHaveAccessToInvoiceAndEquipmentV2 && (
              <Button variant="contained" size="small" onClick={() => handleOpenCreateAndSendQuoteModal({ voId })}>
                Send Quote
              </Button>
            )}

            <Button size="small" disabled={isLoading} onClick={handleOpenInvoiceDetailModal}>
              {isLoading ? 'Updating...' : 'Edit details'}
            </Button>
          </Stack>
        </Box>

        {invoice && (
          <InvoiceDetails
            invoice={invoice}
            isLoading={isLoading}
            contentViewType={contentViewType}
            equipmentDetails={equipmentDetails}
          />
        )}

        <Box className={classes.uploadPanel}>
          <Typography component="span" variant="subtitle2">
            Upload Invoice
          </Typography>

          {!invoiceDoc ? (
            <Box className={classes.emptyDocView}>
              <Typography variant="body2" color="textSecondary">
                The invoice/quote is not uploaded yet
              </Typography>

              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => handleOpenUploadInvoiceDialog({ VOId: voId, dynamicsVOId })}
              >
                Upload Invoice/Quote
              </Button>
            </Box>
          ) : (
            <UploadedFileView
              isDeleting={isDeleting}
              type={invoiceDoc?.type}
              name={invoiceDoc?.name}
              uploadedBy={invoiceDoc?.createdBy}
              onDownload={handleInvoiceDownload}
              uploadedAt={invoiceDoc?.createdDateTime}
              onDelete={() =>
                handleDeleteInvoiceDocument(invoiceDoc.documentId, api => ({
                  refetchQueries: [api.getInvoiceDocument],
                  awaitRefetchQueries: true,
                }))
              }
            />
          )}
        </Box>
      </Box>
    </>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '12px',
    display: 'flex',
    marginTop: '16px',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  panel: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  panelDesktop: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  panelMobile: {
    gap: '4px',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  uploadPanel: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  emptyDocView: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
});
